import { z } from 'zod';

/**
 * Zod schema for the stagery_session Clerk JWT template payload.
 * Mirrors the template definition from Phase 10 D-04 (stagery-api/lib/clerk.ts).
 *
 * NOTE: Once @stagery/stagery-contracts publishes StagerySessionClaimsSchema
 * (10-03), replace this local definition with the import:
 *   import { StagerySessionClaimsSchema, type StagerySessionClaims }
 *     from '@stagery/stagery-contracts';
 *
 * Tracked: Phase 10 D-07 deviation — schema inlined because contracts v1.0.0
 * does not yet export StagerySessionClaimsSchema.
 */
// kantoor_id: Clerk publicMetadata stores this as a number, but new users may
// have undefined (not yet synced) or Clerk may coerce to string. Accept all
// three; normalise to number|null for consumers.
const coercedNullableInt = z
  .union([z.number(), z.string().transform(Number), z.null()])
  .nullable()
  .optional()
  .transform((v) => (v === undefined || v === null || Number.isNaN(v as number) ? null : Number(v)));

export const StagerySessionClaimsSchema = z.object({
  kantoor_id: coercedNullableInt,
  // Non-staff users have null for app_role — nullable() prevents schema mismatch
  // warnings on every page load for regular users.
  app_role: z.enum(['superadmin', 'beheerder', 'medewerker']).nullable(),
  // credits may be null/undefined on brand-new users whose metadata hasn't
  // been synced yet. Preserve explicit null from Clerk for non-staff users;
  // only default missing claims to 0 for older token templates.
  credits: z.number().nullable().optional().transform((v) => v === undefined ? 0 : v),
  org_id: z.string().nullable().optional(),
  // org_role and org_permissions are present when user belongs to a Clerk org.
  // org_permissions defaults to [] — matches contracts StagerySessionClaimsSchema.
  // Audit HIGH #9: these fields were absent from local schema, causing
  // claims.org_permissions to be typed `undefined` vs the contracts `string[]`.
  org_role: z.string().nullable().optional(),
  // P1-01: The live Clerk JWT returns org_permissions as a space-separated
  // string, while the canonical template uses `{{org.permissions}}` which
  // produces a string[]. We coerce both shapes here so validation never throws
  // on the current token shape.
  // TODO (dashboard): set org_permissions → {{org.permissions}} in the Clerk
  // Dashboard JWT template so the server always emits a proper array.
  org_permissions: z
    .preprocess(
      (v) =>
        typeof v === 'string'
          ? v.split(/[\s,]+/).filter(Boolean)
          : v,
      z.array(z.string()),
    )
    .optional()
    .default([]),
});

export type StagerySessionClaims = z.infer<typeof StagerySessionClaimsSchema>;

/**
 * Fetch the stagery_session JWT template token, decode its payload, validate
 * with StagerySessionClaimsSchema, and return typed claims. Returns null on
 * signed-out / malformed token / schema mismatch — never throws.
 *
 * Source: Phase 10 D-04 (template) + D-07 (frontend consumer).
 */
export async function getStagerySessionClaims(
  getToken: (opts: { template: string }) => Promise<string | null>,
): Promise<StagerySessionClaims | null> {
  try {
    const token = await getToken({ template: 'stagery_session' });
    if (!token) return null;
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    const json = JSON.parse(
      atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')),
    );
    const parsed = StagerySessionClaimsSchema.safeParse(json);
    if (!parsed.success) {
      console.warn('[getStagerySessionClaims] schema mismatch', parsed.error.issues);
      return null;
    }
    return parsed.data;
  } catch (err) {
    console.warn('[getStagerySessionClaims] parse failed', err);
    return null;
  }
}
